import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { invitationApi } from '../services/api';

// Handles the Emergent Google OAuth return: exchanges the one-time
// session_id (URL fragment) for app auth cookies, then lands on the dashboard.
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { googleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;

    // Remove session_id from the URL so it's never reused or bookmarked
    window.history.replaceState(null, '', window.location.pathname + window.location.search);

    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    const run = async () => {
      try {
        await googleSession(sessionId);

        const inviteToken = sessionStorage.getItem('pendingInvitation');
        if (inviteToken) {
          try {
            const response = await invitationApi.accept(inviteToken);
            sessionStorage.removeItem('pendingInvitation');
            toast.success('Invitation accepted! You now have access to the estate.');
            navigate(`/estates/${response.data.estate_id}`, { replace: true });
            return;
          } catch (inviteError) {
            const message = inviteError.response?.data?.detail || '';
            if (message.includes('different email')) {
              toast.error('This invitation was sent to a different email address.');
            }
            sessionStorage.removeItem('pendingInvitation');
          }
        }

        navigate('/dashboard', { replace: true });
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-ep-background" data-testid="auth-callback-screen">
      <Loader2 className="w-8 h-8 animate-spin text-ep-primary mb-4" />
      <p className="text-ep-text-secondary">Signing you in with Google…</p>
    </div>
  );
}
